import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useNavigate, useLocation } from '@common/hooks';
import { Colors, FontWeight, LineHeight } from '@common/tokens';
import { Link, Animation, IconOfKind } from '@common/components';
import { Dropdown } from 'antd';
import { VerticalMenuFooter } from './_vertical-menu-footer';
import { VerticalMenuItemHeader } from './_vertical-menu-item-header';
import {
  VerticalMenuSecondLevelType,
  VerticalMenuItemsType,
} from './vertical-menu-types';
import {
  findActiveParentMenu,
  getHorizontalCollapseLocalStorage,
  mapItemsWithChildrenWithLevel,
  setHorizontalCollapseLocalStorage,
  calcChildrenHeight,
  findFirstLevelParentItem,
} from './_vertical-menu-service';

type Props = {
  items: VerticalMenuItemsType[];
  menuKey?: string;
  animationDuration?: number;
  onHorizontalCollapse?: (toggle: boolean) => void;
};

export function VerticalMenuNav({
  items,
  menuKey,
  animationDuration = 200,
  onHorizontalCollapse,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();

  const initialMapState = mapItemsWithChildrenWithLevel(
    items as VerticalMenuSecondLevelType[]
  );
  const [collapseState, setCollapseState] = useState(initialMapState);

  const initialHorizontalCollapse = getHorizontalCollapseLocalStorage(menuKey);
  const [isHorizontalCollapsed, setIsHorizontalCollapsed] = useState(
    initialHorizontalCollapse
  );
  const [isHorizontalCollapsedDelayed, setIsHorizontalCollapsedDelayed] =
    useState(initialHorizontalCollapse);

  function isValidMenuItemKey(menuKey: string) {
    return Object.keys(initialMapState).includes(menuKey);
  }

  useEffect(() => {
    if (isHorizontalCollapsed) {
      setTimeout(
        () => setIsHorizontalCollapsedDelayed(true),
        animationDuration
      );
      return;
    }

    setIsHorizontalCollapsedDelayed(false);
  }, [isHorizontalCollapsed, animationDuration]);

  useEffect(() => {
    const activeItems = items.filter((item) => item.canShow !== false);
    const activeParentKey = findActiveParentMenu(
      1,
      location.pathname,
      activeItems
    );

    if (activeParentKey && isValidMenuItemKey(activeParentKey)) {
      setCollapseState({
        ...collapseState,
        [activeParentKey as string]: true,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleItemClick(level: number, item?: VerticalMenuSecondLevelType) {
    if (item?.children) {
      const parentItem = findFirstLevelParentItem(items, item.name);
      const activeParentKey = `1-${parentItem?.name}`;
      const activeItemKey = `${level}-${item.name}`;

      setCollapseState({
        ...initialMapState,
        [activeParentKey]: level === 2,
        [activeItemKey]: true,
      });
    } else if (level === 1) {
      setCollapseState(initialMapState);
    }

    if (item?.redirectTo) {
      navigate(item?.redirectTo || '');
      return;
    }

    // open the first child that has route
    if (item?.children) {
      const [firstChild] = item.children;
      handleItemClick(level + 1, firstChild);
    }
  }

  function isRouteActive(route?: string) {
    return !!route && location.pathname.includes(route);
  }

  function hasAnyChildActive(
    children?: VerticalMenuSecondLevelType[]
  ): boolean {
    if (!children) return false;

    return children.some(
      (child) =>
        isRouteActive(child.redirectTo) || hasAnyChildActive(child.children)
    );
  }

  function internalOnHorizontalCollapse() {
    onHorizontalCollapse?.(!isHorizontalCollapsed);
    setIsHorizontalCollapsed(!isHorizontalCollapsed);

    if (menuKey) {
      setHorizontalCollapseLocalStorage(menuKey, !isHorizontalCollapsed);
    }
  }

  function isActive(route?: string, children?: VerticalMenuSecondLevelType[]) {
    return isRouteActive(route) && !hasAnyChildActive(children);
  }

  function getCollapseState(level: number, name: string) {
    return collapseState[`${level}-${name}`];
  }

  const containerWidth = isHorizontalCollapsedDelayed ? '54px' : '200px';

  function verticalCollapseStyle(
    level: number,
    item: VerticalMenuSecondLevelType
  ) {
    return {
      maxHeight: getCollapseState(level, item.name)
        ? calcChildrenHeight(item.children)
        : 0,
    };
  }

  return (
    <>
      <div className="wk-vertical-menu-nav" style={{ width: containerWidth }}>
        <Animation
          kind="shrink-horizontal"
          active={isHorizontalCollapsed}
          thresholds={{ shrinkHorizontal: { end: 54 } }}
        >
          <ul className="first-level">
            {items
              .filter((item) => item.canShow !== false)
              .map((itemFirstLevel) => (
                <li
                  key={itemFirstLevel.name}
                  title={
                    isHorizontalCollapsed ? itemFirstLevel.name : undefined
                  }
                  className={clsx({
                    active:
                      isActive(
                        itemFirstLevel.redirectTo,
                        itemFirstLevel.children
                      ) ||
                      (hasAnyChildActive(itemFirstLevel.children) &&
                        isHorizontalCollapsedDelayed),
                    expanded:
                      getCollapseState(1, itemFirstLevel.name) &&
                      !isHorizontalCollapsedDelayed,
                  })}
                >
                  <Dropdown
                    placement="bottomLeft"
                    overlayStyle={{ minWidth: 0 }}
                    disabled={
                      !isHorizontalCollapsedDelayed || !itemFirstLevel.children
                    }
                    menu={{
                      items: buildDropdownItems(itemFirstLevel.children),
                    }}
                  >
                    <div>
                      <VerticalMenuItemHeader
                        onClick={() =>
                          handleItemClick(
                            1,
                            itemFirstLevel as VerticalMenuSecondLevelType
                          )
                        }
                        iconOfKind={itemFirstLevel.iconOfKind as IconOfKind}
                        name={itemFirstLevel.name}
                        hasChildren={!!itemFirstLevel.children}
                        isExpanded={getCollapseState(1, itemFirstLevel.name)}
                        isActive={isActive(
                          itemFirstLevel.redirectTo,
                          itemFirstLevel.children
                        )}
                        isHorizontalCollapsed={isHorizontalCollapsedDelayed}
                      />
                    </div>
                  </Dropdown>
                  {itemFirstLevel.children && !isHorizontalCollapsedDelayed && (
                    <ul
                      className="second-level"
                      style={verticalCollapseStyle(
                        1,
                        itemFirstLevel as VerticalMenuSecondLevelType
                      )}
                    >
                      {itemFirstLevel.children
                        .filter((item) => item.canShow !== false)
                        .map((itemSecondLevel) => (
                          <li
                            key={itemSecondLevel.name}
                            className={clsx({
                              active: isActive(
                                itemSecondLevel.redirectTo,
                                itemSecondLevel.children
                              ),
                            })}
                          >
                            <VerticalMenuItemHeader
                              onClick={() =>
                                handleItemClick(2, itemSecondLevel)
                              }
                              name={itemSecondLevel.name}
                              hasChildren={!!itemSecondLevel.children}
                              isExpanded={getCollapseState(
                                2,
                                itemSecondLevel.name
                              )}
                              isActive={isActive(
                                itemSecondLevel.redirectTo,
                                itemSecondLevel.children
                              )}
                            />
                            {itemSecondLevel.children && (
                              <ul
                                className="third-level"
                                style={verticalCollapseStyle(
                                  2,
                                  itemSecondLevel
                                )}
                              >
                                {itemSecondLevel.children
                                  .filter((item) => item.canShow !== false)
                                  .map((itemThirdLevel) => (
                                    <li
                                      key={itemThirdLevel.name}
                                      className={clsx({
                                        active: isActive(
                                          itemThirdLevel.redirectTo
                                        ),
                                      })}
                                    >
                                      <VerticalMenuItemHeader
                                        onClick={() =>
                                          handleItemClick(3, itemThirdLevel)
                                        }
                                        name={itemThirdLevel.name}
                                        isActive={isActive(
                                          itemThirdLevel.redirectTo
                                        )}
                                        hasChildren={false}
                                        isExpanded={false}
                                      />
                                    </li>
                                  ))}
                              </ul>
                            )}
                          </li>
                        ))}
                    </ul>
                  )}
                </li>
              ))}
          </ul>
          <footer>
            <Animation
              active={isHorizontalCollapsed}
              kind="shrink-horizontal"
              style={{ position: 'relative' }}
              thresholds={{ shrinkHorizontal: { end: 54 } }}
            >
              <VerticalMenuFooter
                isHorizontalCollapsed={isHorizontalCollapsed}
                onHorizontalCollapse={internalOnHorizontalCollapse}
              />
            </Animation>
          </footer>
        </Animation>
      </div>
      <style jsx>{`
        .wk-vertical-menu-nav {
          height: 100%;
          max-width: 200px;
          position: fixed;
          left: 80px;
          top: calc(var(--wk-header-height, 72px) + 24px);

          color: ${Colors.elm};
          font-weight: ${FontWeight.light};
          line-height: ${LineHeight.sm};
        }

        .wk-vertical-menu-nav footer {
          height: 40px;
          width: ${isHorizontalCollapsedDelayed ? '53px' : '199px'};
          max-width: 199px;
          min-width: 53px;
          position: fixed;
          bottom: 0;
          left: 80px;
        }

        .wk-vertical-menu-nav ul {
          /* -1px because the border right */
          width: calc(${containerWidth} - 1px);
          transition: max-height 200ms linear;
          overflow: hidden;
          cursor: pointer;
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .wk-vertical-menu-nav
          li.expanded
          :global(.vertical-menu-item-header:hover),
        .wk-vertical-menu-nav
          li.expanded
          li.active
          :global(.vertical-menu-item-header) {
          background-color: ${Colors.elmLightest};
        }

        .wk-vertical-menu-nav li.active :global(.vertical-menu-item-header) {
          background-color: ${Colors.elmLightest};
        }

        .wk-vertical-menu-nav li :global(.vertical-menu-item-header:hover),
        .wk-vertical-menu-nav li.expanded :global(.vertical-menu-item-header) {
          background-color: ${Colors.elmHyperlight};
        }

        .wk-vertical-menu-nav
          li.active
          li
          :global(.vertical-menu-item-header:hover) {
          background-color: ${Colors.elmLightest};
        }

        /* identation */
        .wk-vertical-menu-nav li li :global(.vertical-menu-item-header) {
          padding-left: 40px;
        }

        .wk-vertical-menu-nav li li li :global(.vertical-menu-item-header) {
          padding-left: 48px;
        }
      `}</style>
    </>
  );
}

function buildDropdownItems(
  items?: VerticalMenuSecondLevelType[]
): { key: string; value?: React.ReactNode }[] | undefined {
  if (!items) return undefined;

  return items
    .filter((item) => item.canShow !== false)
    .map((item) => ({
      key: item.name,
      label: <Link to={item.redirectTo || ''}>{item.name}</Link>,
      children: item.children && buildDropdownItems(item.children),
    }));
}
